import { useState } from "react";
import axios from "axios";
import { Loader2, Mail, Phone, Linkedin, Send } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (form.name.trim().length < 2) {
      toast.error("Merci d'indiquer votre nom.");
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) {
      toast.error("Merci d'indiquer une adresse email valide.");
      return;
    }
    if (form.company.trim().length < 2) {
      toast.error("Merci d'indiquer votre société.");
      return;
    }
    if (form.message.trim().length < 10) {
      toast.error("Merci d'écrire un message un peu plus détaillé.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        message: form.message.trim(),
      });
      toast.success("Message envoyé. Je reviens vers vous rapidement.");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error(
        "L'envoi a échoué. Vous pouvez m'écrire directement à olivier@soutarson.fr."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 sm:py-36 px-6 sm:px-10 lg:px-16 bg-[#F0ECE1]"
    >
      <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-16">
        {/* Left - intro */}
        <div className="lg:col-span-5 reveal">
          <p className="eyebrow flex items-center mb-6">
            <span className="gold-line" />
            Échange confidentiel
          </p>
          <h2 className="font-serif font-light text-4xl sm:text-5xl text-[#0B132B] leading-[1.1] mb-6">
            Parlons de votre <span className="italic">situation.</span>
          </h2>
          <p className="text-[#4A5568] leading-relaxed font-light text-lg mb-10">
            Un échange confidentiel, sans engagement.
            Je reviens vers vous sous 24 heures ouvrées.
          </p>

          <div className="space-y-5">
            <a
              href="mailto:olivier@soutarson.fr"
              data-testid="contact-email-link"
              className="flex items-center gap-4 text-[#0B132B] hover:text-[#C8A97E] transition-colors group"
            >
              <span className="w-10 h-10 border border-[#0B132B] flex items-center justify-center group-hover:border-[#C8A97E] transition-colors">
                <Mail size={16} />
              </span>
              <span className="font-light text-lg">olivier@soutarson.fr</span>
            </a>
            <a
              href="tel:+33645139330"
              data-testid="contact-phone-link"
              className="flex items-center gap-4 text-[#0B132B] hover:text-[#C8A97E] transition-colors group"
            >
              <span className="w-10 h-10 border border-[#0B132B] flex items-center justify-center group-hover:border-[#C8A97E] transition-colors">
                <Phone size={16} />
              </span>
              <span className="font-light text-lg">06&nbsp;45&nbsp;13&nbsp;93&nbsp;30</span>
            </a>
            <a
              href="https://www.linkedin.com/in/olivier-g-soutarson/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-linkedin-link"
              className="flex items-center gap-4 text-[#0B132B] hover:text-[#C8A97E] transition-colors group"
            >
              <span className="w-10 h-10 border border-[#0B132B] flex items-center justify-center group-hover:border-[#C8A97E] transition-colors">
                <Linkedin size={16} />
              </span>
              <span className="font-light text-lg">LinkedIn — Olivier Soutarson</span>
            </a>
          </div>
        </div>

        {/* Right - form */}
        <form
          onSubmit={onSubmit}
          data-testid="contact-form"
          className="lg:col-span-7 reveal bg-[#FAF8F5] p-8 sm:p-12 border border-[#E2DCD0] space-y-8"
        >
          <p
            data-testid="contact-promise"
            className="font-serif italic text-[#0B132B] text-lg sm:text-xl leading-snug border-l-2 border-[#C8A97E] pl-4 -mt-2"
          >
            Votre demande est traitée avec attention. Une réponse personnalisée
            vous est apportée sous <span className="whitespace-nowrap">24 à 48&nbsp;heures</span> ouvrées.
          </p>

          <p
            data-testid="contact-required-note"
            className="text-xs text-[#4A5568] font-light"
          >
            Les champs marqués d'un{" "}
            <span className="text-[#C0392B] font-medium">*</span> sont obligatoires.
          </p>

          <div>
            <label
              htmlFor="contact-name"
              className="block text-xs uppercase tracking-[0.16em] text-[#4A5568] mb-3"
            >
              Nom <span aria-hidden="true" className="text-[#C0392B]">*</span>
            </label>
            <input
              id="contact-name"
              data-testid="contact-name-input"
              type="text"
              value={form.name}
              onChange={update("name")}
              placeholder="Votre nom"
              className="w-full bg-transparent border-b border-[#E2DCD0] focus:border-[#C8A97E] py-3 text-base text-[#171D2A] placeholder:text-[#4A5568]/60 transition-colors"
              autoComplete="name"
              required
              aria-required="true"
            />
          </div>

          <div>
            <label
              htmlFor="contact-email"
              className="block text-xs uppercase tracking-[0.16em] text-[#4A5568] mb-3"
            >
              Email <span aria-hidden="true" className="text-[#C0392B]">*</span>
            </label>
            <input
              id="contact-email"
              data-testid="contact-email-input"
              type="email"
              value={form.email}
              onChange={update("email")}
              placeholder="prenom.nom@entreprise.com"
              className="w-full bg-transparent border-b border-[#E2DCD0] focus:border-[#C8A97E] py-3 text-base text-[#171D2A] placeholder:text-[#4A5568]/60 transition-colors"
              autoComplete="email"
              required
              aria-required="true"
            />
          </div>

          <div>
            <label
              htmlFor="contact-company"
              className="block text-xs uppercase tracking-[0.16em] text-[#4A5568] mb-3"
            >
              Société <span aria-hidden="true" className="text-[#C0392B]">*</span>
            </label>
            <input
              id="contact-company"
              data-testid="contact-company-input"
              type="text"
              value={form.company}
              onChange={update("company")}
              placeholder="Nom de votre société"
              className="w-full bg-transparent border-b border-[#E2DCD0] focus:border-[#C8A97E] py-3 text-base text-[#171D2A] placeholder:text-[#4A5568]/60 transition-colors"
              autoComplete="organization"
              required
              aria-required="true"
            />
          </div>

          <div>
            <label
              htmlFor="contact-message"
              className="block text-xs uppercase tracking-[0.16em] text-[#4A5568] mb-3"
            >
              Message <span aria-hidden="true" className="text-[#C0392B]">*</span>
            </label>
            <textarea
              id="contact-message"
              data-testid="contact-message-input"
              value={form.message}
              onChange={update("message")}
              placeholder="Quelques mots sur votre contexte et ce que vous cherchez."
              rows={5}
              className="w-full bg-transparent border-b border-[#E2DCD0] focus:border-[#C8A97E] py-3 text-base text-[#171D2A] placeholder:text-[#4A5568]/60 transition-colors resize-none"
              required
              aria-required="true"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit-button"
            className="group inline-flex items-center gap-3 bg-[#0B132C] text-[#FAF8F5] px-8 py-4 text-xs uppercase tracking-[0.18em] font-medium hover:bg-[#16234A] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Envoi en cours
              </>
            ) : (
              <>
                <Send size={16} className="transition-transform group-hover:translate-x-1" />
                Envoyer ma demande
              </>
            )}
          </button>

          <p className="text-xs text-[#4A5568] font-light pt-2">
            Vos coordonnées restent confidentielles et ne sont utilisées
            que pour répondre à votre demande.
          </p>
        </form>
      </div>
    </section>
  );
}
